import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  NoSsr,
  Skeleton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AuthService from '@services/AuthService';
import ErrorMessage from '@components/shared/ErrorMessage';
import Link from '@components/shared/Link';
import SelectRoles from '@main/login/SelectRoles';
import { AUTH_MODE } from '@utils/constants';
import { flexCenter, flexColumnBetween } from '@utils/utility-classes';
import { isLocalhost } from '@utils/Functions';
import { MainRoutes } from '@routes';
import { useSnackBars } from '@contexts/Snackbar/SnackBarContext';

const defaultValues = {
  IdUser: isLocalhost() ? '1413' : '',
  User: isLocalhost() ? '1413' : '',
  roles: 'employee',
};

export default function Login() {
  const router = useRouter();
  const { debug = false, redirectTo = false } = router.query;
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const selectedRoles = useRef([]).current;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onBlur',
    defaultValues,
  });
  const { showErrorSnackbar } = useSnackBars();

  if (errors?.length > 0) console.error(errors);

  const onSubmit = async (values) => {
    if (!router?.isReady) return false;
    const credentials = buildRequestForm(values);
    await logIn(credentials);
  };

  const buildRequestForm = (formValues) => {
    let requestValues = {
      username: formValues.User,
      password: formValues.Password,
    };
    if (debug) {
      requestValues.id_user = formValues.IdUser || defaultValues.IdUser;
      requestValues.username = formValues.User || defaultValues.User;
      requestValues.roles = selectedRoles.toString() || defaultValues.roles;
      requestValues.authentication = AUTH_MODE.INTERNAL;
    }
    return requestValues;
  };

  const { mutateAsync: logIn } = useMutation(
    (credentials) => AuthService.login(credentials),
    {
      onSuccess: onLoginSuccess,
      onError: showErrorSnackbar,
    },
  );

  function onLoginSuccess(response) {
    AuthService.saveTokenInLocalStorage(response);
    redirectToPageLogged();
  }

  const redirectToPageLogged = () => {
    const urlForRedirect = redirectTo || MainRoutes.home;
    router.replace(urlForRedirect);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} method='post'>
      {process.env.NODE_ENV === 'development' && (
        <>
          <Link
            sx={{ color: 'common.blue', fontSize: 14 }}
            to={
              redirectTo
                ? '/?debug=true&redirectTo=' + redirectTo
                : '/?debug=true'
            }
          >
            Debug mode
          </Link>
          <br />
          <p>1463: days: carrera_afin: true, ongoing_studies: true </p>
          <p>1413: days: carrera_afin: false, ongoing_studies: true </p>
          <p>1478: super_admin </p>
          <a
            href='https://docs.google.com/document/d/1P88CpRZRoVaZu3mDDVOQbw56b9iAx_2mCvWNHI5_Pro/edit'
            target='user_tests'
          >
            Usuarios de prueba
          </a>
        </>
      )}
      <Box sx={{ ...flexColumnBetween, mb: 4 }}>
        <NoSsr
          fallback={
            <Skeleton
              variant='rectangular'
              height={54}
              sx={{
                m: '16px 0',
                width: { xs: 'calc(100vw - 40px)', sm: '400px' },
                maxWidth: { xs: '400px', sm: 'none' },
              }}
            />
          }
        >
          <FormControl variant='standard' sx={{ m: '16px 0', maxWidth: 400 }}>
            <InputLabel htmlFor='User'>Usuario ARSAT</InputLabel>
            <Input
              id='User'
              style={{ margin: 0 }}
              sx={{
                border: 'none',
                fontSize: { xs: 14, sm: 16 },
                height: 54,
                maxWidth: '90vw',
                width: 400,
              }}
              {...register('User', { required: 'Debe ingresar su usuario.' })}
              autoFocus //eslint-disable-line jsx-a11y/no-autofocus
              placeholder='Usuario'
              type='text'
            />
            <ErrorMessage errors={errors} name='User' />
          </FormControl>
        </NoSsr>
        {!debug && (
          <NoSsr
            fallback={
              <Skeleton
                variant='rectangular'
                height={54}
                sx={{
                  m: '16px 0 32px',
                  width: { xs: 'calc(100vw - 40px)', sm: '400px' },
                  maxWidth: { xs: '400px', sm: 'none' },
                }}
              />
            }
          >
            <FormControl variant='standard' sx={{ m: '16px 0', maxWidth: 400 }}>
              <InputLabel htmlFor='Password'>Contraseña</InputLabel>
              <Input
                {...register('Password', {
                  required: 'Debe ingresar su contraseña.',
                })}
                id='Password'
                sx={{
                  border: 'none',
                  fontSize: { xs: 14, sm: 16 },
                  height: 54,
                  maxWidth: '90vw',
                  width: 400,
                }}
                style={{ margin: 0, marginBottom: '16px' }}
                placeholder='Contraseña'
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={toggleShowPassword}
                      aria-label={
                        showPassword
                          ? 'Mostrar contraseña'
                          : 'Ocultar Contraseña'
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <ErrorMessage errors={errors} name='Password' />
            </FormControl>
          </NoSsr>
        )}
        {debug && (
          <NoSsr
            fallback={
              <Skeleton
                variant='rectangular'
                height={54}
                sx={{
                  m: '16px 0 32px',
                  width: { xs: 'calc(100vw - 40px)', sm: '400px' },
                  maxWidth: { xs: '400px', sm: 'none' },
                }}
              />
            }
          >
            <FormControl variant='standard' sx={{ m: '16px 0', maxWidth: 400 }}>
              <InputLabel htmlFor='component-simple'>ID Usuario</InputLabel>
              <Input
                sx={{
                  border: 'none',
                  fontSize: { xs: 14, sm: 16 },
                  height: 54,
                  maxWidth: '90vw',
                  width: 400,
                }}
                style={{ margin: 0 }}
                {...register('IdUser')}
                autoFocus //eslint-disable-line jsx-a11y/no-autofocus
                placeholder='ID Usuario'
                type='text'
              />
              <ErrorMessage errors={errors} name='IdUser' />
            </FormControl>
            <SelectRoles selectedRoles={selectedRoles} register={register} />
          </NoSsr>
        )}
      </Box>
      <Box
        sx={{
          ...flexCenter,
          flexDirection: 'column',
          height: { xs: 80, sm: 'auto' },
        }}
      >
        <NoSsr
          fallback={
            <Button type='submit' sx={{ width: 180, my: 2 }} disabled={true}>
              Ingresar
            </Button>
          }
        >
          <Button
            type='submit'
            sx={{ width: 180, my: 2, '&:hover': { bgcolor: 'common.blue' } }}
            disabled={!router?.isReady}
            loading={isSubmitting}
          >
            Ingresar
          </Button>
        </NoSsr>
      </Box>
    </form>
  );
}
